import { keyBy, uniq, sortBy } from 'lodash-es'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import tableLayoutService from 'Services/api/tableLayout.service'
import { UPDATE_TABLE_LAYOUT_CACHE } from 'store/actions/tableLayout.action'
import { useAppSelector } from 'store/useStoreHook'
import { Dictionary } from 'utilities/typeUtility/typeUtility'

function useTableLayoutDisplay() {
    const dispatch = useDispatch()
    const tableLayouts = useAppSelector(
        (state) => state.tableLayout.tableLayouts,
    )

    const refetchTableLayouts = useCallback(() => {
        tableLayoutService.getTableLayouts().then((res) => {
            dispatch({ type: UPDATE_TABLE_LAYOUT_CACHE, payload: res.data })
        })
    }, [dispatch])

    const displaySeatNote = useCallback(
        (unparsedSeatNote: string) => {
            const notes = unparsedSeatNote.split(',')
            const tableMap = keyBy(
                tableLayouts.flatMap((l) => l.tables ?? []),
                'tableID',
            )
            const noteGroupedByTable: Dictionary<number[]> = {}
            const results: string[] = []

            notes.forEach((note) => {
                const [tableId, unparsedSeatNo] = note.split('@')
                if (!tableId || !unparsedSeatNo) return

                if (!tableMap[tableId]) return

                const seatNo = Number(unparsedSeatNo)
                if (isNaN(seatNo)) return

                if (!noteGroupedByTable[tableId])
                    noteGroupedByTable[tableId] = [seatNo]
                else noteGroupedByTable[tableId].push(seatNo)
            })

            // remove duplicate
            Object.keys(noteGroupedByTable).forEach((tableId) => {
                noteGroupedByTable[tableId] = uniq(noteGroupedByTable[tableId])
            })

            if (tableLayouts.length > 1) {
                const layoutMap = keyBy(tableLayouts, 'tableLayoutID')
                Object.keys(noteGroupedByTable).forEach((tableId) => {
                    const tableLayoutID = tableMap[tableId].tableLayoutID!
                    const selectedSeatString = sortBy(
                        noteGroupedByTable[tableId],
                    ).join(', ')
                    results.push(
                        `${selectedSeatString} - Table ${tableMap[tableId].name} (${layoutMap[tableLayoutID].name})`,
                    )
                })
            } else {
                const groupedTableIds = Object.keys(noteGroupedByTable)
                const hasMultipleTable = groupedTableIds.length > 1
                groupedTableIds.forEach((tableId) => {
                    const selectedSeatString = sortBy(
                        noteGroupedByTable[tableId],
                    ).join(', ')
                    results.push(
                        `${selectedSeatString}${
                            hasMultipleTable
                                ? ` - Table ${tableMap[tableId].name}`
                                : ''
                        }`,
                    )
                })
            }

            results.sort((a, b) => a.localeCompare(b))
            return results.join(' / ')
        },
        [tableLayouts],
    )

    return {
        refetchTableLayouts,
        displaySeatNote,
    } as const
}

export default useTableLayoutDisplay
